import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { Observable, of } from "rxjs";
import { catchError, map, shareReplay } from "rxjs/operators";

/**
 * Custom (non-font) icons available to <bv-icon svgIcon="…">. Mirrors the
 * set previously registered with Material's MatIconRegistry. SVG files are
 * fetched from /assets at runtime and cached.
 */
const DEFAULT_ICONS: Record<string, string> = {
  facebook: "/assets/icons/facebook.svg",
  twitter: "/assets/icons/twitter.svg",
  calculator: "/assets/icons/calculator.svg",
  rebuy: "/assets/icons/jetons-orange.svg",
  addon: "/assets/icons/jetons-bleu.svg",
  balance: "/assets/icons/balance.svg",
  "add-table": "/assets/icons/add-table.svg",
  level: "/assets/icons/level.svg",
  "break-table": "/assets/icons/break-table.svg",
  "draw-seats": "/assets/icons/draw-seats.svg",
  multiplier: "/assets/icons/multiplier.svg",
  "down-chevron": "/assets/icons/icon-chevron-down.svg",
  hand_for_hand_on: "/assets/icons/hand_for_hand_on.svg",
  hand_for_hand_off: "/assets/icons/hand_for_hand_off.svg",
  "eliminated-by": "/assets/icons/eliminated-by.svg",
  printer: "/assets/icons/printer.svg",
  bounty: "/assets/icons/bounty.svg",
};

@Injectable({ providedIn: "root" })
export class BvIconRegistry {
  private readonly urls = new Map<string, string>(
    Object.entries(DEFAULT_ICONS)
  );
  private readonly cache = new Map<string, Observable<SafeHtml>>();

  constructor(
    private readonly http: HttpClient,
    private readonly sanitizer: DomSanitizer
  ) {}

  /** Register or override a custom SVG icon by name. */
  register(name: string, url: string): void {
    this.urls.set(name, url);
    this.cache.delete(name);
  }

  /** Fetch the inline SVG markup for a registered icon (cached). */
  get(name: string): Observable<SafeHtml> {
    const cached = this.cache.get(name);
    if (cached) return cached;

    const url = this.urls.get(name);
    if (!url) return of("" as unknown as SafeHtml);

    const request = this.http.get(url, { responseType: "text" }).pipe(
      map((svg) => this.sanitizer.bypassSecurityTrustHtml(svg)),
      catchError(() => of("" as unknown as SafeHtml)),
      shareReplay(1)
    );
    this.cache.set(name, request);
    return request;
  }
}
